import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MoneyInput } from '../components/MoneyInput';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  AlertTriangle, Download, FileText, FileSpreadsheet, Search, DollarSign,
  ChevronUp, ChevronDown, Loader2, Clock, Filter, X,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  resident:        { label: 'Residente',    cls: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' },
  responsible:     { label: 'Responsable',  cls: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300' },
  health_insurer:  { label: 'Obra Social',  cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' },
};

const fmt = (n) => `$ ${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  return isNaN(d) ? '—' : d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

function userCan(user, mod, act) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[mod]?.[act] === true;
}

export default function Debtors() {
  const { user } = useOutletContext();
  const [morosos, setMorosos] = useState({ open: false, rows: [], totals: null, labels: {}, buckets: [] });
  const [data, setData] = useState({ rows: [], totals: {}, by_type: {}, buckets: [], labels: {} });
  const [loading, setLoading] = useState(true);
  const [debtorType, setDebtorType] = useState('all');
  const [minBalance, setMinBalance] = useState('0.01');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('total_debt');
  const [sortDir, setSortDir] = useState('desc');

  const canView = userCan(user, 'deudores', 'ver');
  const canExport = userCan(user, 'deudores', 'exportar');
  const canCollect = userCan(user, 'tesoreria_cobranzas', 'crear');

  const [services, setServices] = useState([]);
  const [serviceIds, setServiceIds] = useState([]); // array de service_id seleccionados
  const [serviceSearch, setServiceSearch] = useState('');
  const [residents, setResidents] = useState([]);
  const [residentIds, setResidentIds] = useState([]);
  const [residentSearch, setResidentSearch] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailCcOverride, setDetailCcOverride] = useState(false);
  const [detailAggregate, setDetailAggregate] = useState(false);

  useEffect(() => {
    if (!canView) return;
    axios.get(`${BACKEND_URL}/api/resident-services`, { withCredentials: true })
      .then(r => setServices(r.data || []))
      .catch(() => {});
    axios.get(`${BACKEND_URL}/api/residents?limit=2000`, { withCredentials: true })
      .then(r => setResidents(r.data || []))
      .catch(() => {});
  }, [canView]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { min_balance: minBalance || 0.01 };
      if (debtorType !== 'all') params.debtor_type = debtorType;
      if (serviceIds.length) params.service_ids = serviceIds.join(',');
      if (residentIds.length) params.resident_ids = residentIds.join(',');
      const r = await axios.get(`${BACKEND_URL}/api/debtors/aging`, { params, withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error('No se pudo cargar el aging');
    } finally {
      setLoading(false);
    }
  }, [debtorType, minBalance, serviceIds, residentIds]);

  useEffect(() => { if (canView) load(); }, [load, canView]);

  const sortedRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    let rows = data.rows;
    if (term) {
      rows = rows.filter(r => r.debtor_name?.toLowerCase().includes(term) || r.identifier?.toLowerCase().includes(term));
    }
    return [...rows].sort((a, b) => {
      const av = a[sortBy] ?? 0;
      const bv = b[sortBy] ?? 0;
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data.rows, search, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const SortArrow = ({ col }) => sortBy === col ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3 inline ml-1" /> : <ChevronDown className="w-3 h-3 inline ml-1" />) : null;

  const buildUrl = (path) => {
    const url = new URL(`${BACKEND_URL}${path}`);
    url.searchParams.set('min_balance', minBalance || 0.01);
    if (debtorType !== 'all') url.searchParams.set('debtor_type', debtorType);
    if (serviceIds.length) url.searchParams.set('service_ids', serviceIds.join(','));
    return url.toString();
  };

  const toggleService = (sid) => {
    setServiceIds(prev => prev.includes(sid) ? prev.filter(s => s !== sid) : [...prev, sid]);
  };
  const clearServices = () => setServiceIds([]);
  const filteredServices = useMemo(() => {
    const t = serviceSearch.trim().toLowerCase();
    const sorted = [...services].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'));
    return t ? sorted.filter(s => (s.name || '').toLowerCase().includes(t) || (s.code || '').toLowerCase().includes(t)) : sorted;
  }, [services, serviceSearch]);

  const toggleResident = (rid) => {
    setResidentIds(prev => prev.includes(rid) ? prev.filter(r => r !== rid) : [...prev, rid]);
  };
  const clearResidents = () => setResidentIds([]);
  const filteredResidents = useMemo(() => {
    const t = residentSearch.trim().toLowerCase();
    const sorted = [...residents].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'es'));
    return t ? sorted.filter(r => (r.full_name || '').toLowerCase().includes(t) || (r.dni || '').toLowerCase().includes(t)) : sorted;
  }, [residents, residentSearch]);

  const buildDetailExportUrl = (ext) => {
    const url = new URL(`${BACKEND_URL}/api/debtors/by-resident-service/${ext}`);
    url.searchParams.set('min_balance', Number(minBalance) || 0.01);
    if (serviceIds.length) url.searchParams.set('service_ids', serviceIds.join(','));
    if (residentIds.length) url.searchParams.set('resident_ids', residentIds.join(','));
    if (detailCcOverride) url.searchParams.set('cc_override_only', 'true');
    return url.toString();
  };

  const openDetail = async (overrideOnly = false, aggregate = false) => {
    setDetailCcOverride(overrideOnly);
    setDetailAggregate(aggregate);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const params = { min_balance: Number(minBalance) || 0.01 };
      if (serviceIds.length) params.service_ids = serviceIds.join(',');
      if (residentIds.length) params.resident_ids = residentIds.join(',');
      if (overrideOnly) params.cc_override_only = true;
      if (aggregate) params.aggregate_by_resident = true;
      const r = await axios.get(`${BACKEND_URL}/api/debtors/by-resident-service`, { params, withCredentials: true });
      setDetailData(r.data);
    } catch (e) {
      toast.error('No se pudo cargar el listado detallado');
    } finally { setDetailLoading(false); }
  };

  const openCollect = (row) => {
    if (row.debtor_type === 'health_insurer') {
      // Llevamos a la página de OS con flag para abrir recibo
      window.location.href = `/health-insurers?open_cc=${row.debtor_id}`;
    } else if (row.debtor_type === 'resident') {
      window.location.href = `/residents?open_collect=${row.debtor_id}`;
    } else {
      // Responsable: hoy se cobra desde el residente al que pertenece
      toast.info('Los cobros a responsables se hacen desde el módulo Residentes del residente asociado.');
    }
  };

  const openMorosos = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/billing/aging?min_balance=0.01`, { withCredentials: true });
      setMorosos({ open: true, rows: r.data.rows || [], totals: r.data.totals, labels: r.data.labels || {}, buckets: r.data.buckets || [] });
    } catch (e) { toast.error('Error al cargar el reporte de morosos'); }
  };

  if (!canView) {
    return <div className="p-6"><Card><CardContent className="p-6 text-center text-slate-500">Sin permisos para ver deudores.</CardContent></Card></div>;
  }

  return (
    <div className="space-y-4" data-testid="debtors-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            Deudores por Ventas
          </h1>
          <p className="text-sm text-slate-500">Aging unificado de Residentes, Responsables y Obras Sociales.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => openDetail(false, true)} data-testid="detail-residents-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" title="Un residente por fila. Si filtrás por concepto, muestra solo los residentes con deuda en ese concepto (incluyendo adicionales con CC-override).">
            <FileText className="w-4 h-4 mr-1" /> Residentes por Concepto
          </Button>
          <Button variant="outline" size="sm" onClick={() => openDetail(true, false)} data-testid="detail-cc-override-btn" className="border-amber-300 text-amber-700 hover:bg-amber-50" title="Facturas emitidas al convenio pero cuya deuda vive en la CC del residente (adicionales).">
            <AlertTriangle className="w-4 h-4 mr-1" /> FC a Convenio · CC a Residente
          </Button>
          <Button variant="outline" size="sm" onClick={openMorosos} data-testid="morosos-btn">
            <AlertTriangle className="w-4 h-4 mr-1" /> Morosos
          </Button>
          {canExport && (
            <>
              <Button variant="outline" size="sm" onClick={() => window.open(buildUrl('/api/debtors/aging/pdf'), '_blank')} data-testid="export-pdf">
                <FileText className="w-4 h-4 mr-1" /> PDF
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.open(buildUrl('/api/debtors/aging/excel'), '_blank')} data-testid="export-excel">
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Tipo de deudor</Label>
              <Select value={debtorType} onValueChange={setDebtorType}>
                <SelectTrigger className="w-[180px]" data-testid="filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="resident">Residente</SelectItem>
                  <SelectItem value="responsible">Responsable</SelectItem>
                  <SelectItem value="health_insurer">Obra Social</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Saldo mínimo</Label>
              <MoneyInput
                value={minBalance} onChange={(e) => setMinBalance(e.target.value)}
                className="w-[120px]" data-testid="filter-min-balance"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Buscar (nombre o documento)</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-3 text-slate-400" />
                <Input
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  className="pl-8" placeholder="Ej: García, 12345678, 30-..."
                  data-testid="filter-search"
                />
              </div>
            </div>
            <div className="min-w-[220px]">
              <Label className="text-xs">Residentes</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between" data-testid="filter-residents-btn">
                    <span className="flex items-center gap-2">
                      <Filter className="w-3.5 h-3.5" />
                      {residentIds.length === 0 ? 'Todos los residentes' : `${residentIds.length} seleccionado(s)`}
                    </span>
                    {residentIds.length > 0 && (
                      <X className="w-3.5 h-3.5 opacity-60 hover:opacity-100"
                         onClick={(e) => { e.stopPropagation(); clearResidents(); }} />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[340px] p-0" align="start">
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
                      <Input
                        className="pl-7 h-8 text-xs"
                        placeholder="Buscar residente…"
                        value={residentSearch}
                        onChange={(e) => setResidentSearch(e.target.value)}
                        data-testid="filter-residents-search"
                      />
                    </div>
                    <div className="flex items-center justify-between pt-2 text-[11px]">
                      <span className="text-slate-500">
                        {filteredResidents.length} de {residents.length} · {residentIds.length} seleccionado(s)
                      </span>
                      {residentIds.length > 0 && (
                        <button className="text-rose-600 hover:underline"
                          onClick={clearResidents} data-testid="filter-residents-clear">
                          Limpiar
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto p-1">
                    {filteredResidents.map(r => (
                      <label
                        key={r.resident_id}
                        className="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-800 rounded cursor-pointer"
                        data-testid={`filter-resident-${r.resident_id}`}
                      >
                        <Checkbox
                          checked={residentIds.includes(r.resident_id)}
                          onCheckedChange={() => toggleResident(r.resident_id)}
                        />
                        <span className="flex-1 truncate">
                          {r.dni && <span className="font-mono text-[10px] text-slate-500 mr-1">[{r.dni}]</span>}
                          {r.full_name}
                        </span>
                      </label>
                    ))}
                    {filteredResidents.length === 0 && (
                      <p className="text-xs text-slate-400 text-center py-4">Sin resultados</p>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="min-w-[220px]">
              <Label className="text-xs">Conceptos (catálogo)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between" data-testid="filter-concepts-btn">
                    <span className="flex items-center gap-2">
                      <Filter className="w-3.5 h-3.5" />
                      {serviceIds.length === 0 ? 'Todos los conceptos' : `${serviceIds.length} seleccionado(s)`}
                    </span>
                    {serviceIds.length > 0 && (
                      <X className="w-3.5 h-3.5 opacity-60 hover:opacity-100"
                         onClick={(e) => { e.stopPropagation(); clearServices(); }} />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[340px] p-0" align="start">
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
                      <Input
                        className="pl-7 h-8 text-xs"
                        placeholder="Buscar concepto…"
                        value={serviceSearch}
                        onChange={(e) => setServiceSearch(e.target.value)}
                        data-testid="filter-concepts-search"
                      />
                    </div>
                    <div className="flex items-center justify-between pt-2 text-[11px]">
                      <span className="text-slate-500">
                        {filteredServices.length} de {services.length} · {serviceIds.length} seleccionado(s)
                      </span>
                      {serviceIds.length > 0 && (
                        <button className="text-rose-600 hover:underline"
                          onClick={clearServices} data-testid="filter-concepts-clear">
                          Limpiar
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto p-1">
                    {filteredServices.map(s => (
                      <label
                        key={s.service_id}
                        className="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-800 rounded cursor-pointer"
                        data-testid={`filter-concept-${s.service_id}`}
                      >
                        <Checkbox
                          checked={serviceIds.includes(s.service_id)}
                          onCheckedChange={() => toggleService(s.service_id)}
                        />
                        <span className="flex-1 truncate">
                          {s.code && <span className="font-mono text-[10px] text-slate-500 mr-1">[{s.code}]</span>}
                          {s.name}
                        </span>
                      </label>
                    ))}
                    {filteredServices.length === 0 && (
                      <p className="text-xs text-slate-400 text-center py-4">Sin resultados</p>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <Button size="sm" onClick={load} variant="outline">Actualizar</Button>
          </div>
          {serviceIds.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2" data-testid="filter-concepts-chips">
              {serviceIds.map(sid => {
                const s = services.find(x => x.service_id === sid);
                return (
                  <Badge key={sid} variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-300 text-[10px] gap-1 pr-1">
                    {s?.name || sid}
                    <button onClick={() => toggleService(sid)} className="ml-0.5 hover:text-rose-700">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          )}
          {residentIds.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2" data-testid="filter-residents-chips">
              {residentIds.map(rid => {
                const r = residents.find(x => x.resident_id === rid);
                return (
                  <Badge key={rid} variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 text-[10px] gap-1 pr-1">
                    {r?.full_name || rid}
                    <button onClick={() => toggleResident(rid)} className="ml-0.5 hover:text-rose-700">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          )}
        </CardHeader>
      </Card>

      {/* Tabla principal */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : sortedRows.length === 0 ? (
            <div className="p-8 text-center text-slate-500">Sin deudores con los filtros actuales 🎉</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="cursor-pointer" onClick={() => toggleSort('debtor_name')}>
                      Deudor <SortArrow col="debtor_name" />
                    </TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Documento</TableHead>
                    {data.buckets?.map(b => (
                      <TableHead key={b} className="text-right cursor-pointer" onClick={() => toggleSort(b)}>
                        {data.labels?.[b] || b} <SortArrow col={b} />
                      </TableHead>
                    ))}
                    <TableHead className="text-right cursor-pointer" onClick={() => toggleSort('total_debt')}>
                      Total <SortArrow col="total_debt" />
                    </TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => toggleSort('oldest_days_overdue')}>
                      Días <SortArrow col="oldest_days_overdue" />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRows.map(r => {
                    const meta = TYPE_META[r.debtor_type] || { label: r.debtor_type, cls: 'bg-slate-100' };
                    return (
                      <TableRow key={`${r.debtor_type}-${r.debtor_id}`} data-testid={`debtor-row-${r.debtor_id}`}>
                        <TableCell className="font-medium">{r.debtor_name}</TableCell>
                        <TableCell><Badge variant="outline" className={`text-[10px] ${meta.cls}`}>{meta.label}</Badge></TableCell>
                        <TableCell className="text-xs font-mono">{r.identifier}</TableCell>
                        {data.buckets?.map(b => (
                          <TableCell key={b} className="text-right text-xs">
                            {r[b] > 0 ? fmt(r[b]) : <span className="text-slate-300">—</span>}
                          </TableCell>
                        ))}
                        <TableCell className="text-right font-bold text-amber-700 dark:text-amber-400">{fmt(r.total_debt)}</TableCell>
                        <TableCell className="text-right">
                          {r.oldest_days_overdue > 0 ? (
                            <Badge variant="outline" className={r.oldest_days_overdue > 90 ? 'bg-red-100 text-red-700' : r.oldest_days_overdue > 30 ? 'bg-amber-100 text-amber-700' : ''}>
                              {r.oldest_days_overdue}d
                            </Badge>
                          ) : <span className="text-slate-300">—</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Fila de totales */}
                  <TableRow className="bg-amber-50 dark:bg-amber-950/30 font-bold">
                    <TableCell colSpan={3}>TOTAL ({sortedRows.length} deudores)</TableCell>
                    {data.buckets?.map(b => (
                      <TableCell key={b} className="text-right">
                        {data.totals?.[b] > 0 ? fmt(data.totals[b]) : '—'}
                      </TableCell>
                    ))}
                    <TableCell className="text-right text-amber-800 dark:text-amber-300">{fmt(data.totals?.total_debt)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ServicesHeatmap debtorType={debtorType} minBalance={minBalance} serviceIds={serviceIds} />

      {/* Dialog: Listado detallado Residente + Concepto */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detailCcOverride
                ? <><AlertTriangle className="w-5 h-5 text-amber-600" /> FC a Convenio · Deuda en CC del Residente</>
                : detailAggregate
                  ? <><FileText className="w-5 h-5 text-emerald-600" /> Residentes con deuda {serviceIds.length ? `(filtrado por ${serviceIds.length} concepto${serviceIds.length>1?'s':''})` : ''}</>
                  : <><FileText className="w-5 h-5 text-indigo-600" /> Deuda detallada por Residente + Concepto</>}
            </DialogTitle>
            <p className="text-xs text-slate-500">
              {detailCcOverride
                ? 'Solo facturas emitidas a un convenio (p. ej. adicionales) cuya deuda vive en la cuenta corriente del residente.'
                : detailAggregate
                  ? 'Un residente por fila. Si filtrás por concepto arriba, verás solo los residentes que adeudan ese concepto (incluye adicionales facturados al convenio con CC al residente).'
                  : 'Saldo pendiente de cada factura prorrateado por el peso de sus renglones, agrupado por residente y concepto.'}
              {' '}Respeta los filtros de arriba (conceptos, residentes, saldo mínimo).
            </p>
          </DialogHeader>

          {detailLoading ? (
            <div className="p-8 text-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Calculando…
            </div>
          ) : detailData ? (
            <>
              {detailAggregate && canExport && (
                <div className="flex justify-end gap-2 mb-2" data-testid="detail-export-bar">
                  <Button size="sm" variant="outline" onClick={() => window.open(buildDetailExportUrl('pdf'), '_blank')} data-testid="detail-export-pdf" className="border-rose-300 text-rose-700 hover:bg-rose-50">
                    <FileText className="w-4 h-4 mr-1" /> PDF
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(buildDetailExportUrl('excel'), '_blank')} data-testid="detail-export-excel" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                    <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                  </Button>
                </div>
              )}
              <div className={`grid ${detailAggregate ? 'grid-cols-3' : 'grid-cols-4'} gap-3 mb-3`}>
                <Card><CardContent className="pt-4">
                  <p className="text-xs text-slate-500">Total adeudado</p>
                  <p className="text-xl font-bold text-amber-700" data-testid="detail-total-outstanding">{fmt(detailData.totals.total_outstanding)}</p>
                  {detailData.totals.total_credit_lines ? (
                    <p className="text-[10px] text-slate-500 mt-0.5" data-testid="detail-total-credit-lines">
                      Comprobantes {fmt(detailData.totals.total_gross_debt)} · sin imputar <span className="text-emerald-700">{fmt(detailData.totals.total_credit_lines)}</span>
                    </p>
                  ) : null}
                </CardContent></Card>
                <Card><CardContent className="pt-4">
                  <p className="text-xs text-slate-500">Filas</p>
                  <p className="text-xl font-bold">{detailData.totals.row_count}</p>
                </CardContent></Card>
                <Card><CardContent className="pt-4">
                  <p className="text-xs text-slate-500">Residentes</p>
                  <p className="text-xl font-bold">{detailData.totals.resident_count}</p>
                </CardContent></Card>
                {!detailAggregate && (
                  <Card><CardContent className="pt-4">
                    <p className="text-xs text-slate-500">FC-Convenio · CC-Residente</p>
                    <p className="text-xl font-bold text-amber-700">{fmt(detailData.totals.total_insurer_billed_resident_cc)}</p>
                  </CardContent></Card>
                )}
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Residente</TableHead>
                    <TableHead>DNI</TableHead>
                    {detailAggregate && <TableHead>Fecha Egreso</TableHead>}
                    {!detailAggregate && <TableHead>Concepto</TableHead>}
                    {detailAggregate && <TableHead>Conceptos adeudados</TableHead>}
                    <TableHead className="text-right">FCs</TableHead>
                    <TableHead className="text-right">Días (max)</TableHead>
                    <TableHead>Circuito</TableHead>
                    <TableHead className="text-right">Monto adeudado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailData.rows.length === 0 ? (
                    <TableRow><TableCell colSpan={detailAggregate ? 8 : 7} className="text-center text-slate-500 py-6">Sin resultados con estos filtros</TableCell></TableRow>
                  ) : detailData.rows.map((r, i) => (
                    <TableRow key={detailAggregate ? r.resident_id : `${r.resident_id}-${r.service_id}`} data-testid={`detail-row-${i}`}>
                      <TableCell className="font-medium">{r.resident_name}</TableCell>
                      <TableCell className="font-mono text-xs">{r.dni}</TableCell>
                      {detailAggregate && (
                        <TableCell className="text-xs whitespace-nowrap align-top">
                          {r.discharge_date ? <span className="text-rose-700">{fmtDate(r.discharge_date)}</span> : <span className="text-slate-300">—</span>}
                        </TableCell>
                      )}
                      {!detailAggregate && (
                        <TableCell className={`text-xs ${r.is_credit_line ? 'text-emerald-700 italic' : ''}`} data-testid={r.is_credit_line ? `detail-credit-line-${i}` : undefined}>
                          {r.service_code && <span className="font-mono text-[10px] text-slate-500 mr-1">[{r.service_code}]</span>}
                          {r.service_name}
                        </TableCell>
                      )}
                      {detailAggregate && (
                        <TableCell className="text-xs align-top">
                          <div className="space-y-1.5" data-testid={`detail-concepts-${i}`}>
                            {(r.concepts || []).map((c, k) => (
                              <div key={k} className={`rounded-md border overflow-hidden ${c.is_credit_line ? 'border-emerald-200' : 'border-slate-200 dark:border-slate-700'}`} data-testid={c.is_credit_line ? `detail-credit-line-${i}` : undefined}>
                                <div className={`flex items-center justify-between gap-2 px-2 py-1 ${c.is_credit_line ? 'bg-emerald-50 dark:bg-emerald-950/30' : 'bg-slate-50 dark:bg-slate-800'}`}>
                                  <span className={`font-medium ${c.is_credit_line ? 'text-emerald-800 italic' : ''}`}>
                                    {c.service_code && <span className="font-mono text-[10px] text-slate-500 mr-1">[{c.service_code}]</span>}
                                    {c.service_name}
                                  </span>
                                  <span className={`font-semibold whitespace-nowrap ${c.is_credit_line ? 'text-emerald-700' : 'text-amber-700'}`}>{fmt(c.outstanding)}</span>
                                </div>
                                {(c.invoices || []).length > 0 && (
                                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {(c.invoices || []).map((inv, m) => (
                                      <div key={m} className="flex items-center justify-between gap-2 px-2 py-0.5 text-[10px] text-slate-600 dark:text-slate-300" data-testid={`detail-concept-inv-${i}-${k}-${m}`}>
                                        <span className="whitespace-nowrap">{fmtDate(inv.issue_date)}</span>
                                        <span className="font-mono flex-1 truncate">{inv.invoice_number || '—'}</span>
                                        <span className="whitespace-nowrap">{fmt(inv.share)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="text-right text-xs">{r.invoice_count}</TableCell>
                      <TableCell className="text-right">
                        {r.max_days_overdue > 0 ? (
                          <Badge variant="outline" className={r.max_days_overdue > 90 ? 'bg-red-100 text-red-700 text-[10px]' : r.max_days_overdue > 30 ? 'bg-amber-100 text-amber-700 text-[10px]' : 'text-[10px]'}>
                            {r.max_days_overdue}d
                          </Badge>
                        ) : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.has_insurer_billed_resident_cc && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 text-[10px] mr-1" title="Adicional al convenio con CC al residente">
                            Adicional
                          </Badge>
                        )}
                        {r.has_tpp && (
                          <Badge variant="outline" className="bg-violet-50 text-violet-800 border-violet-300 text-[10px]" title="Pagador de Terceros (Curaduría)">
                            TPP
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className={`text-right font-bold whitespace-nowrap ${r.outstanding < 0 ? 'text-emerald-700' : 'text-amber-700'}`}>{fmt(r.outstanding)}</TableCell>
                    </TableRow>
                  ))}
                  {detailData.rows.length > 0 && (
                    <TableRow className="bg-amber-50 dark:bg-amber-950/30 font-bold">
                      <TableCell colSpan={detailAggregate ? 6 : 6}>TOTAL ({detailData.totals.row_count} {detailAggregate ? 'residentes' : 'líneas'} · {detailData.totals.resident_count} residentes)</TableCell>
                      <TableCell className="text-right text-amber-800">{fmt(detailData.totals.total_outstanding)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={morosos.open} onOpenChange={(o) => !o && setMorosos({ ...morosos, open: false })}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="morosos-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />Reporte de Morosos — Antigüedad de saldos
          </DialogTitle></DialogHeader>
          <div className="flex justify-end gap-2 mb-2">
            <Button size="sm" variant="outline" onClick={() => window.open(`${BACKEND_URL}/api/billing/aging/pdf`, '_blank')} data-testid="morosos-pdf"><FileText className="w-4 h-4 mr-1" />PDF</Button>
            <Button size="sm" variant="outline" onClick={() => window.open(`${BACKEND_URL}/api/billing/aging/excel`, '_blank')} data-testid="morosos-excel"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Residente</TableHead><TableHead>DNI/CUIT</TableHead>
              {morosos.buckets.map(b => <TableHead key={b} className="text-right">{morosos.labels[b]}</TableHead>)}
              <TableHead className="text-right">Total</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {morosos.rows.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-slate-500">Sin deudores</TableCell></TableRow> :
                morosos.rows.map(x => (
                  <TableRow key={x.resident_id} data-testid={`moroso-${x.resident_id}`}>
                    <TableCell className="font-medium">{x.full_name}</TableCell>
                    <TableCell className="font-mono text-xs">{x.dni || '—'}</TableCell>
                    {morosos.buckets.map(b => <TableCell key={b} className={`text-right text-xs ${b === 'd90p' && x[b] > 0 ? 'text-red-600 font-semibold' : ''}`}>{x[b] ? fmt(x[b]) : '—'}</TableCell>)}
                    <TableCell className="text-right font-bold">{fmt(x.total_debt)}</TableCell>
                  </TableRow>
                ))}
              {morosos.totals && morosos.rows.length > 0 && (
                <TableRow className="bg-muted/50 font-bold">
                  <TableCell colSpan={2}>TOTALES</TableCell>
                  {morosos.buckets.map(b => <TableCell key={b} className="text-right text-xs">{fmt(morosos.totals[b])}</TableCell>)}
                  <TableCell className="text-right" data-testid="morosos-total">{fmt(morosos.totals.total_debt)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const CELL_STYLE = {
  por_vencer: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300',
  d0_30: 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300',
  d31_60: 'bg-orange-100 text-orange-800 dark:bg-orange-950/30 dark:text-orange-300',
  d61_90: 'bg-red-100 text-red-800 dark:bg-red-950/30 dark:text-red-300',
  d90p: 'bg-red-200 text-red-900 dark:bg-red-900/40 dark:text-red-200',
};
const HEAD_STYLE = {
  por_vencer: 'text-emerald-700 dark:text-emerald-400',
  d0_30: 'text-amber-700 dark:text-amber-400',
  d31_60: 'text-orange-700 dark:text-orange-400',
  d61_90: 'text-red-700 dark:text-red-400',
  d90p: 'text-red-800 dark:text-red-300',
};

function ServicesHeatmap({ debtorType, minBalance, serviceIds }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { min_balance: Number(minBalance) || 0.01 };
      if (debtorType !== 'all') params.debtor_type = debtorType;
      if (serviceIds && serviceIds.length) params.service_ids = serviceIds.join(',');
      const r = await axios.get(`${BACKEND_URL}/api/debtors/aging/by-service`, { params, withCredentials: true });
      setData(r.data);
    } catch (e) { toast.error('Error al cargar el mapa de calor por servicio'); }
    finally { setLoading(false); }
  }, [debtorType, minBalance, serviceIds]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="services-heatmap">
      <CardHeader className="pb-2">
        <h2 className="text-base font-semibold flex items-center gap-2">
          <Clock className="w-4 h-4 text-amber-500" />
          Mapa de Calor <span className="text-slate-400 font-normal">— Deuda por Servicio</span>
        </h2>
        <p className="text-xs text-slate-500">
          Cantidad de deudores y saldo de cada servicio del catálogo, por antigüedad. El saldo de
          cada factura se distribuye entre sus renglones.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? <p className="text-center text-slate-500 py-6" data-testid="heatmap-loading">Cargando…</p>
          : !data?.rows?.length ? <p className="text-center text-slate-500 py-6" data-testid="heatmap-empty">Sin deuda por servicio en este filtro.</p>
            : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="uppercase text-xs">Servicio</TableHead>
                      {data.buckets.map(b => (
                        <TableHead key={b} className={`text-center uppercase text-xs ${HEAD_STYLE[b] || ''}`}>
                          {data.labels[b]}
                        </TableHead>
                      ))}
                      <TableHead className="text-right uppercase text-xs">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.rows.map(row => (
                      <TableRow key={row.service_id} data-testid={`heatmap-row-${row.service_id}`}>
                        <TableCell className="font-medium max-w-[240px] truncate" title={row.service_name}>
                          {row.service_name}
                        </TableCell>
                        {data.buckets.map(b => {
                          const c = row.cells[b];
                          return (
                            <TableCell key={b} className="text-center p-1">
                              {c.count === 0 ? <span className="text-slate-300">0</span> : (
                                <div className={`rounded-md py-1.5 px-2 ${CELL_STYLE[b] || ''}`}
                                  data-testid={`heatmap-cell-${row.service_id}-${b}`}>
                                  <span className="font-bold">{c.count}</span>
                                  <span className="text-[10px] ml-1 opacity-80">{fmt(c.amount)}</span>
                                </div>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-right font-bold whitespace-nowrap">
                          {fmt(row.total_amount)}
                          <span className="block text-[10px] font-normal text-slate-500">
                            {row.total_count} deudor(es)
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-amber-50 dark:bg-amber-950/30 font-bold">
                      <TableCell>TOTALES</TableCell>
                      {data.buckets.map(b => (
                        <TableCell key={b} className="text-center text-xs">
                          {data.totals[b].count} · {fmt(data.totals[b].amount)}
                        </TableCell>
                      ))}
                      <TableCell className="text-right text-amber-800 dark:text-amber-300" data-testid="heatmap-total">
                        {fmt(data.totals.total.amount)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
      </CardContent>
    </Card>
  );
}

